import Phaser from 'phaser';
import { VIEWPORT_W } from '../../../constants';
import type { TigerBoogaModel } from '../../types';
import { playTigerAnim, registerTigerAnims, type TigerWorldState } from './tiger-anims';

const TAP_COOLDOWN_MS = 3000;

export function createTigerView(scene: Phaser.Scene, getModel: () => TigerBoogaModel) {
  registerTigerAnims(scene);

  let sprite: Phaser.GameObjects.Sprite | null = null;
  let shadow: Phaser.GameObjects.Image | null = null;
  let state: TigerWorldState = 'crossed';
  let tapCount = 0;
  let tapCooldownTimer: Phaser.Time.TimerEvent | null = null;
  let angryTimer: Phaser.Time.TimerEvent | null = null;
  let panelVisible = true;

  function layoutTiger() {
    if (!sprite) return;
    const { x, y, scale } = getModel();
    sprite.setPosition(x, y);
    sprite.setScale(scale);
    sprite.setOrigin(0.5, 1);

    if (shadow) {
      const refScale = 0.3;
      const ratio = refScale > 0 ? scale / refScale : 1;
      const shadowW = 146 * ratio;
      const shadowH = 16 * ratio;
      shadow.setDisplaySize(shadowW, shadowH);
      shadow.setPosition(x, y - 12 * ratio - shadowH * 0.5);
      shadow.setOrigin(0.5, 0.5);
    }
  }

  function clearTapCooldown() {
    tapCooldownTimer?.remove(false);
    tapCooldownTimer = null;
  }

  function clearAngryTimer() {
    angryTimer?.remove(false);
    angryTimer = null;
  }

  function setState(next: TigerWorldState) {
    if (state === next || !sprite) return;
    state = next;
    playTigerAnim(sprite, state);
    layoutTiger();
  }

  function coolDown() {
    if (state !== 'angry') return;
    clearAngryTimer();
    tapCount = 0;
    setState('crossed');
  }

  function makeAngry() {
    const model = getModel();
    if (state === 'angry' || !sprite) return;
    clearTapCooldown();
    tapCount = 0;
    setState('angry');
    clearAngryTimer();
    angryTimer = scene.time.delayedCall(model.angryDurationMs, coolDown);
  }

  function resetTapCountLater() {
    clearTapCooldown();
    tapCooldownTimer = scene.time.delayedCall(TAP_COOLDOWN_MS, () => {
      tapCount = 0;
    });
  }

  function wiggleOnTap() {
    if (!sprite) return;
    const { scale } = getModel();
    scene.tweens.add({
      targets: sprite,
      scaleX: scale * 1.03,
      scaleY: scale * 0.97,
      duration: 80,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => layoutTiger(),
    });
  }

  const initial = getModel();
  if (scene.textures.exists('tiger-crossed')) {
    sprite = scene.add.sprite(initial.x, initial.y, 'tiger-crossed').setDepth(11);
    shadow = scene.add.image(initial.x, initial.y, 'geode-shadow').setDepth(10).setAlpha(0.8);
    layoutTiger();
    playTigerAnim(sprite, 'crossed');
  }

  function syncVisibility(scrollX: number) {
    panelVisible = scrollX > VIEWPORT_W * 0.5;
    sprite?.setVisible(panelVisible);
    shadow?.setVisible(panelVisible);
  }

  function hitTest(pointer: Phaser.Input.Pointer) {
    if (!panelVisible || !sprite?.visible || state === 'angry') return false;
    const { x, y, hitRadius } = getModel();
    return Phaser.Math.Distance.Between(pointer.worldX, pointer.worldY, x, y) <= hitRadius;
  }

  function onTap() {
    const model = getModel();
    if (!panelVisible || !sprite || state === 'angry') return false;

    wiggleOnTap();
    tapCount += 1;
    resetTapCountLater();

    if (tapCount >= model.tapsToAngry) {
      makeAngry();
    }
    return true;
  }

  return {
    sprite,
    shadow,
    setModel() {
      layoutTiger();
    },
    syncFromScroll(scrollX: number) {
      syncVisibility(scrollX);
    },
    hitTest,
    onTap,
    destroy() {
      clearTapCooldown();
      clearAngryTimer();
      sprite?.destroy();
      shadow?.destroy();
      sprite = null;
      shadow = null;
    },
  };
}

export type TigerView = ReturnType<typeof createTigerView>;
